<template>
    <button
        class="c-button-wrapper btn"
        :class="`btn-${btnVariant} btn-${size} ${buttonClasses}`"
        :style="gradientStyle"
        :type="type"
        :disabled="disabled"
        @click="(e) => emit('click', e)"
        @mouseover="isHover = true"
        @mouseleave="isHover = false"
        v-bind="ddName ? { 'data-dd-action-name': ddName } : {}"
    >
        <template v-if="!$slots.default">
            <font-awesome-icon
                v-if="icon && iconPosition === 'left'"
                :icon="getVisibleIcon"
                :class="{ 'mr-2': !!text }"
            />
            <span :class="textClasses">{{ text }}</span>
            <font-awesome-icon
                v-if="icon && iconPosition === 'right'"
                :icon="getVisibleIcon"
                class="absolute right-3"
            />
        </template>
        <slot />
    </button>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import type { CButtonVariant } from '.';

const props = withDefaults(
    defineProps<{
        ddName?: string;
        text?: string;
        icon?: string | string[];
        hoverIcon?: string | string[];
        size?: string;
        variant?: CButtonVariant;
        disabled?: boolean;
        type?: 'button' | 'submit';
        iconPosition?: 'left' | 'right';
        gradientColors?: string[];
    }>(),
    {
        type: 'button',
        size: 'md',
        iconPosition: 'left',
        gradientColors: () => ['#7258e0', '#88c3d1']
    }
);

const emit = defineEmits<{
    (event: 'click', e: MouseEvent): void;
}>();

const slots = useSlots();
const isHover = ref(false);

const gradientStyle = computed(() => {
    if (btnVariant.value !== 'gradient') return {};
    return {
        'background-image': `linear-gradient(to right, ${props.gradientColors.join(', ')})`,
        border: 'none',
        color: '#fff'
    };
});

const btnVariant = computed(() => (props.variant?.length ? props.variant : 'empty'));

const getVisibleIcon = computed(() => {
    if (isHover.value && props.hoverIcon) {
        return props.hoverIcon;
    }
    return props.icon;
});

const buttonClasses = computed(() => {
    return props.iconPosition === 'right' ? 'd-flex align-items-center justify-content-center position-relative' : '';
});

const textClasses = computed(() => {
    return props.iconPosition === 'right' ? 'flex-grow-1 text-left' : '';
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/utilities/functions';
.c-button-wrapper {
    font-weight: normal;
    &.btn,
    &:focus,
    &:hover {
        box-shadow: none !important;
    }
    &.btn-empty:hover,
    &.btn-outline-empty:hover {
        background-color: get-color('gray-200');
    }
    &.btn-outline-empty {
        border: 1px solid get-color('gray-light');
    }
    &.btn-icon {
        height: 21px;
        box-sizing: content-box;
        font-size: 0.875rem;
    }
}
</style>
